import React, { useState, useEffect, useRef } from "react"
import { IconPhoneOutgoing, IconMessage2, IconMapPin, IconChevronDown, IconCheck } from "@tabler/icons-react"
import { useParams } from "react-router-dom"
import "../../../../../assets/css/App.css"
import Header from "../../../../../components/Header"
import MapContainer from "../../../../../components/MapContainer"
import Footer from "../../../../../components/Footer"
import Processing from "../../../../../assets/images/Processing.svg"
import Sending from "../../../../../assets/images/Sending.svg"
import Success from "../../../../../assets/images/Success.svg"
import Faild from "../../../../../assets/images/Faild.svg"
import LodingGif from "../../../../../assets/images/logo-loading.gif"
import NotFoundImg from "../../../../../assets/images/404.svg"
import { useDispatch, useSelector } from "react-redux"
import { fetchData } from "../../../../../store/tracking"
import customAxios from "../../../../../configs/axios"
/* ─────────────────────────────────────────────
   ROUTE:  /v1/alarm/:session/:tracking/:phone
   API:    /tracking/getAlarmDataBySession/:session/:tracking/:phone
───────────────────────────────────────────── */

// ── Small reusable helpers ──────────────────────────────────────────────────

const HrLine = () => <div><hr /></div>

const LogHeader = ({ iconColor, iconSize }) => (
	<div className="DetailHeader">
		<div className="contacts">Emergency Contact</div>
		<div className="phone">
			<IconPhoneOutgoing color={iconColor} size={iconSize} />
			<p>Phone</p>
		</div>
		<div className="text">
			<IconMessage2 color={iconColor} size={iconSize} />
			<p>Text</p>
		</div>
		<div className="location">
			<IconMapPin color={iconColor} size={iconSize} />
			<p>Location</p>
		</div>
	</div>
)

const BorderLog = ({ contact_name, call_status, sms_status, map_status }) => (
	<div className="row">
		<div className="contacts">{contact_name}</div>
		<div className="phone">
			{call_status === "answered" || call_status === "completed" ? (
				<img src={Success} alt="Success" />
			) : call_status === "queued" || call_status === "accepted" || call_status === "started" || call_status === "ringing" ? (
				<img src={Sending} alt="Sending" />
			) : (
				<img src={Faild} alt="Failed" />
			)}
		</div>
		<div className="text">
			{sms_status === "delivered" ? (
				<img src={Success} alt="Success" />
			) : sms_status === "queued" || sms_status === "sent" ? (
				<img src={Sending} alt="Sending" />
			) : (
				<img src={Faild} alt="Failed" />
			)}
		</div>
		<div className="location">
			{map_status === 1 ? (
				<img src={Success} alt="Success" />
			) : (
				<img src={Processing} alt="Processing" className="processing-spin" />
			)}
		</div>
	</div>
)

const DividerLine = () => (
	<div>
		<div className="vertical-line"></div>
		<hr className="horizontal-line" />
	</div>
)

// ── Ordinal helper  (1 → "1st", 2 → "2nd", …) ────────────────────────────
function ordinal(n) {
	const s = ["th", "st", "nd", "rd"]
	const v = n % 100
	return n + (s[(v - 20) % 10] || s[v] || s[0])
}

// ── Trigger type badge ─────────────────────────────────────────────────────
const BADGE_STYLES = {
	touch: { background: "#E8FBF0", color: "#1A9E4A", dot: "#1A9E4A" },
	inner: { background: "#FFF4E5", color: "#D97706", dot: "#D97706" },
	"inner border": { background: "#FFF4E5", color: "#D97706", dot: "#D97706" },
	outer: { background: "#F3EDFF", color: "#7C3AED", dot: "#7C3AED" },
	"outer border": { background: "#F3EDFF", color: "#7C3AED", dot: "#7C3AED" },
}

const TriggerBadge = ({ type = "" }) => {
	const key = type.toLowerCase()
	const style = BADGE_STYLES[key] || { background: "#F3F4F6", color: "#6B7280", dot: "#6B7280" }
	const label = key.charAt(0).toUpperCase() + key.slice(1) + (key.includes("border") ? "" : " border")

	return (
		<span style={{
			display: "inline-flex",
			alignItems: "center",
			gap: "5px",
			padding: "2px 10px",
			borderRadius: "20px",
			fontSize: "12px",
			fontWeight: 500,
			background: style.background,
			color: style.color,
		}}>
			<span style={{
				width: 7, height: 7,
				borderRadius: "50%",
				background: style.dot,
				flexShrink: 0,
			}} />
			{label}
		</span>
	)
}

// ── Format ISO timestamp → "2025-09-22, 17:06:21" ─────────────────────────
function formatDateTime(iso) {
	if (!iso) return "—"
	const d = new Date(iso)
	const ymd = d.toISOString().slice(0, 10)
	const hms = d.toTimeString().slice(0, 8)
	return `${ymd}, ${hms}`
}

// ── Format trigger time for the alarm card header ─────────────────────────
function formatTriggerTime(iso) {
	if (!iso) return ""
	const d = new Date(iso)
	const hours = d.getHours() % 12 || 12
	const mins = d.getMinutes().toString().padStart(2, "0")
	const amOrPm = d.getHours() >= 12 ? "PM" : "AM"
	return `${hours}:${mins} ${amOrPm}`
}

// ── Alarms Logs filter dropdown ────────────────────────────────────────────
const FILTER_OPTIONS = ["All trigers", "Touch", "Inner border", "Outer border"]

function TriggersDropdown({ value, onChange }) {
	const [open, setOpen] = useState(false)
	const ref = useRef(null)

	useEffect(() => {
		function handleClickOutside(e) {
			if (ref.current && !ref.current.contains(e.target)) setOpen(false)
		}
		document.addEventListener("mousedown", handleClickOutside)
		return () => document.removeEventListener("mousedown", handleClickOutside)
	}, [])

	return (
		<div ref={ref} style={{ position: "relative", display: "inline-block" }}>
			<button
				onClick={() => setOpen(o => !o)}
				style={{
					display: "inline-flex",
					alignItems: "center",
					gap: "4px",
					background: "transparent",
					border: "none",
					cursor: "pointer",
					fontSize: "14px",
					color: "#374151",
					fontWeight: 500,
					padding: "0",
				}}
			>
				{value}
				<IconChevronDown size={14} />
			</button>

			{open && (
				<div style={{
					position: "absolute",
					top: "calc(100% + 6px)",
					left: 0,
					background: "#fff",
					border: "1px solid #E5E7EB",
					borderRadius: "8px",
					boxShadow: "0 4px 12px rgba(0,0,0,.1)",
					zIndex: 50,
					minWidth: "150px",
					overflow: "hidden",
				}}>
					{FILTER_OPTIONS.map(opt => (
						<button
							key={opt}
							onClick={() => { onChange(opt); setOpen(false) }}
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								width: "100%",
								padding: "10px 14px",
								background: opt === value ? "#F9FAFB" : "transparent",
								border: "none",
								cursor: "pointer",
								fontSize: "14px",
								color: "#374151",
								textAlign: "left",
							}}
						>
							{opt}
							{opt === value && <IconCheck size={14} color="#1A9E4A" />}
						</button>
					))}
				</div>
			)}
		</div>
	)
}

// ── Alarms Logs section ────────────────────────────────────────────────────
function AlarmsLogs({ triggers = [], alarmId }) {
	const [filter, setFilter] = useState("All trigers")

	const filtered = filter === "All trigers"
		? triggers
		: triggers.filter(t => {
			const key = filter.toLowerCase()
			return t.alarm_type.toLowerCase() === key ||
				(t.alarm_type.toLowerCase() + " border") === key
		})

	return (
		<div className="AlarmsLogsSection">
			<h2 style={{ fontSize: "22px", fontWeight: 700, margin: "0 0 4px", color: "#111827" }}>
				Alarms Logs
			</h2>

			<p style={{ fontSize: "13px", color: "#6B7280", margin: "0 0 20px" }}>
				Alarm session ID : #{alarmId}
			</p>

			{/* Table header */}
			<div
				className="alarm-log-header"
				style={{
					display: "grid",
					gridTemplateColumns: "180px 1fr auto",
					padding: "10px 20px",
					gap: "12px",
				}}
			>
				<span style={{ fontSize: "13px", color: "#6B7280" }}>
					Emergency Contact
				</span>

				<span
					style={{
						fontSize: "13px",
						color: "#6B7280",
						position: "relative",
					}}
				>
					<TriggersDropdown value={filter} onChange={setFilter} />
				</span>

				<span style={{ fontSize: "13px", color: "#6B7280" }}>
					Time &amp; Date
				</span>
			</div>

			<div
				style={{
					border: "1px solid #EEEEEE",
					borderRadius: "15px",
					overflow: "hidden",
				}}
			>
				{/* Rows */}
				{filtered.length === 0 ? (
					<div
						style={{
							padding: "20px",
							textAlign: "center",
							color: "#9CA3AF",
							fontSize: "14px",
						}}
					>
						No triggers found for this filter.
					</div>
				) : (
					filtered.map((trigger, idx) => (
						<div
							key={trigger.id}
							className="alarm-log-row"
							style={{
								display: "grid",
								gridTemplateColumns: "180px 1fr auto",
								padding: "14px 20px",
								borderBottom:
									idx < filtered.length - 1
										? "1px solid #DBE3F0"
										: "none",
								alignItems: "center",
								gap: "12px",
								background: "#fff",
							}}
						>
							{/* Desktop + Mobile Common */}
							<span
								className="alarm-log-use"
								style={{
									fontSize: "14px",
									color: "#374151",
								}}
							>
								{ordinal(idx + 1)} use :
							</span>

							<span className="alarm-trigger-badge">
								<TriggerBadge type={trigger.alarm_type} />
							</span>

							<span
								className="alarm-log-date"
								style={{
									fontSize: "13px",
									color: "#2563EB",
									whiteSpace: "nowrap",
								}}
							>
								{formatDateTime(trigger.trigger_time)}
							</span>
						</div>
					))
				)}
			</div>

			{/* Mobile only CSS */}
			<style>{`
				@media (max-width: 768px) {
					.alarm-log-header {
						grid-template-columns: 1fr auto !important;
						padding: 10px 12px !important;
					}

					.alarm-log-row {
						grid-template-columns: 1fr auto !important;
						padding: 16px 14px !important;
						align-items: flex-start !important;
						row-gap: 10px !important;
						column-gap: 12px !important;
					}

					/* use text */
					.alarm-log-use {
						grid-column: 1 / 2;
						grid-row: 1;
						text-align: left;
					}

					/* badge right side */
					.alarm-trigger-badge {
						grid-column: 2 / 3;
						grid-row: 1 / 3;
						display: flex;
						justify-content: flex-end;
						align-items: flex-start;
					}

					/* date below use */
					.alarm-log-date {
						grid-column: 1 / 2;
						grid-row: 2;
						white-space: normal !important;
						word-break: break-word;
						overflow-wrap: break-word;
						text-align: left !important;
					}

					/* dropdown fix */
					.alarm-log-header .dropdown-menu {
						right: 0 !important;
						left: auto !important;
						max-width: calc(100vw - 24px);
					}
				}
			`}</style>
		</div>
	)
}

// ── Main Tracking component ────────────────────────────────────────────────
function Tracking() {
	const dispatch = useDispatch()
	const [data, setData] = useState(null)
	const [triggerTime, setTriggerTime] = useState("")
	const [baseClassName, setBaseClassName] = useState("container")
	const [isLoading, setIsLoading] = useState(true)
	const [isEmpty, setIsEmpty] = useState(true)

	// Updated route params  →  /v1/alarm/:session/:tracking/:phone
	const { session, tracking, phone } = useParams()

	const iconColor = "#7D7E8A"
	const iconSize = 24

	// Updated API endpoint
	const apiUrl = `/tracking/getAlarmDataBySession/${session}/${tracking}/${phone}`
	const store = useSelector(state => state.tracking)
	const [mapData, setMapData] = useState(null)
	const MapDataURL = `/tracking/getAlarmData/${tracking}/${phone}`

	useEffect(() => {
		const fetchMapData = async () => {
			try {
				const response = await customAxios.get(MapDataURL)
				const json = response.data

				if (json?.success && json?.data?.[0]) {
					console.log("Fetched map data:", json.data[0])
					setMapData(json.data[0])
				} else {
					console.log("No map data found:", json)
					// Fallback to latest session trigger coordinates if getAlarmData returns null
					if (data?.triggers && data.triggers.length > 0) {
						const latestTrigger = [...data.triggers].reverse().find(t => t.lat && t.lng);
						if (latestTrigger) {
							console.log("Fallback to trigger coordinates:", latestTrigger);
							setMapData(latestTrigger);
							return;
						}
					}
					setMapData(null)
				}
			} catch (err) {
				console.error("Map data fetch error:", err)
			}
		}

		fetchMapData()
		const interval = setInterval(fetchMapData, 30000)
		return () => clearInterval(interval)
	}, [MapDataURL, data])  // ← remove mapData from deps, causes infinite loop
	// ── Handle new response structure: data is a plain object, not array ──
	useEffect(() => {
		if (store?.data && typeof store.data === "object" && !Array.isArray(store.data)) {
			// New API returns { …, data: { alarm_id, triggers, … } }
			setData(store.data)
		} else if (store?.data?.[0]) {
			// Backwards-compat: old API returned an array
			setData(store.data[0])
		} else {
			setIsEmpty(true)
		}
		setIsLoading(false)
	}, [store])

	useEffect(() => {
		if (data && Object.keys(data).length > 0) {
			setIsEmpty(false)

			const touchLen = data?.touch_border_logs?.length ?? 0
			const innerLen = data?.inner_border_logs?.length ?? 0

			if ((touchLen > 0) !== (innerLen > 0)) {
				// Only one column → single-column layout
				setBaseClassName("container singleContainer")
			} else {
				setBaseClassName("container")
			}

			// Use first trigger time as the "alarm triggered" time
			const firstTrigger = data?.triggers?.[0]
			if (firstTrigger?.trigger_time) {
				setTriggerTime(formatTriggerTime(firstTrigger.trigger_time))
			} else if (data?.created_at) {
				setTriggerTime(formatTriggerTime(data.created_at))
			}
		}
	}, [data])

	useEffect(() => {
		setIsLoading(true)
		dispatch(fetchData({ apiUrl }))

		const interval = setInterval(() => {
			dispatch(fetchData({ apiUrl }))
		}, 30000)

		return () => clearInterval(interval)
	}, [dispatch, apiUrl])


	// ── Device label: prefer device_name, fall back to device_id ─────────
	const deviceLabel = data?.device_name || data?.device_id || ""

	// ── Alarm text: prefer first trigger's alarm_text, else top-level ─────
	const alarmText = data?.triggers?.[0]?.alarm_text
		?.replace("{TRACKINGURL}", window.location.href) ?? data?.alarm_text ?? ""

	// ── is_test: true if ANY trigger has is_test === true ─────────────────
	const isTest = data?.triggers?.some(t => t.is_test) ?? data?.is_test ?? false

	// ── Touch alarm label: "Test Alarm" / "Touch Alarm" ──────────────────
	const touchAlarmTitle = isTest ? "Test Alarm" : "Touch Alarm"

	return (
		<div>
			{isLoading ? (
				<div className="App LoadingSection">
					<div>
						<img src={LodingGif} alt="Pico Act Tracking" />
						<p>Loading...</p>
					</div>
				</div>
			) : (
				<>
					{!isEmpty ? (
						<div className="App">
							<Header />

							{/* Map */}
							<div className="MapSection">
								<MapContainer data={mapData} />
							</div>

							{/* Message */}
							<div className="MessageSection">
								{mapData?.lat == null || mapData?.lng == null && (
									<p className="error">GPS-coordinates could not be retrieved from the Device</p>
								)}
								<h2 className="main-title">{deviceLabel}</h2>
								<p className="help-message">{alarmText}</p>
							</div>

							<HrLine />

							{/* Touch / Inner border log columns */}
							<div className="DetailSection">
								<div className={baseClassName}>
									{data?.touch_border_logs?.length > 0 && (
										<div className="column left">
											<div className="details">
												<div className="DetailTitle">
													<h2 className="title">{touchAlarmTitle}</h2>
													<div className="time">{triggerTime}</div>
												</div>
												<p>
													Below is the current status of {deviceLabel} emergency contacts that has been notified.
												</p>
											</div>
											<div className="detail">
												<LogHeader iconColor={iconColor} iconSize={iconSize} />
												<div className="DetailBody">
													{data.touch_border_logs.map((log, i) => (
														<BorderLog
															key={i}
															contact_name={log.contact_name}
															sms_status={log.sms_status}
															call_status={log.call_status}
															map_status={log.map_status}
														/>
													))}
												</div>
											</div>
										</div>
									)}

									{data?.touch_border_logs?.length > 0 && data?.inner_border_logs?.length > 0 && (
										<DividerLine />
									)}

									{data?.inner_border_logs?.length > 0 && (
										<div className="column right">
											<div className="details">
												<div className="DetailTitle">
													<h2 className="title">Inner Border</h2>
													<div className="time">{triggerTime}</div>
												</div>
												<p>
													Below is the current status of {deviceLabel} emergency contacts that has been notified.
												</p>
											</div>
											<div className="detail">
												<LogHeader iconColor={iconColor} iconSize={iconSize} />
												<div className="DetailBody">
													{data.inner_border_logs.map((log, i) => (
														<BorderLog
															key={i}
															contact_name={log.contact_name}
															sms_status={log.sms_status}
															call_status={log.call_status}
															map_status={log.map_status}
														/>
													))}
												</div>
											</div>
										</div>
									)}
								</div>
							</div>

							<HrLine />

							{/* ── NEW: Alarms Logs ─────────────────────────── */}
							{data?.triggers?.length > 0 && (
								<>
									<AlarmsLogs
										triggers={data.triggers}
										alarmId={data.alarm_id}
									/>
									{/* <HrLine /> */}
								</>
							)}

							<Footer />
						</div>
					) : (
						<div className="App LoadingSection">
							<div>
								<h2>Page Not Found</h2>
								<p>Oops! 😖 The requested URL was not found on this server.</p>
								<img height="500" alt="error" src={NotFoundImg} />
							</div>
						</div>
					)}
				</>
			)}
		</div>
	)
}

export default Tracking